// Additional steps that make our Vue app run in the browser
import makeStore from 'app/store'
import makeRouter from 'app/router'
import routes from 'app/routes'
import dashboard from 'app/plugins/dashboard'
import SiteLink from 'app/components-wa/nav/SiteLink'
import { createGtag } from 'vue-gtag'
import { WEB_AWESOME_LAYOUT } from 'app/router/layouts'

import InstantSearch from 'vue-instantsearch/vue3/es'

let leakGuardWarned = false

export default (app) => {
  // Vuex 4 and Router 4 are installed on the app instance, not on a global
  // constructor, so neither factory needs it any more.
  const store = makeStore()
  const router = makeRouter(routes)

  app.use(store)
  app.use(router)


  app.use(InstantSearch)

  // DF-001 memory leak: vue-instantsearch's <ais-instant-search> stores the InstantSearch instance
  // in reactive data(), so Vue deep-observes the whole instance — including its `renderState`
  // getter, which builds a fresh per-index results object on every access. Vue observes each fresh
  // copy and they accumulate across searches (multi-GB while typing). On Vue 2.7, observe() skips
  // any object flagged `__v_skip` (the markRaw flag), so we wrap the component's data() (in
  // beforeCreate, before initData runs) to mark the instance before Vue ever observes it. The
  // instance is plumbing, not display data, so it never needs to be reactive.
  app.mixin({
    beforeCreate() {
      const opts = this.$options
      if (opts.name === 'AisInstantSearch' && typeof opts.data === 'function') {
        const data = opts.data
        opts.data = function () {
          const d = data.call(this)
          if (d && d.instantSearchInstance && typeof d.instantSearchInstance === 'object') {
            d.instantSearchInstance.__v_skip = true
          }
          return d
        }
      }
    },
    // Companion to the instance fix above: every vue-instantsearch widget stores its connector
    // render state in a reactive `this.state`, which references the result's `_state`
    // (SearchParameters) -> helper. Freezing the render state before assignment keeps Vue from
    // observing that graph too, so past result sets are released. Runs before the widget mixin's
    // own `created` (so the connector binds the wrapped fn); guarded by the injected instance.
    created() {
      // Both fixes key off vue-instantsearch internals (the AisInstantSearch component name and
      // the injected instance), so a library upgrade can silently defeat them and bring the leak
      // back. Every widget injects instantSearchInstance — if it reaches here unmarked, the
      // beforeCreate data() wrap never engaged; say so loudly in dev.
      if (
        process.env.NODE_ENV !== 'production' &&
        this.instantSearchInstance &&
        this.instantSearchInstance.__v_skip !== true &&
        !leakGuardWarned
      ) {
        leakGuardWarned = true
        console.warn(
          '[bootstrap] DF-001 leak guard did not engage: the InstantSearch instance was not ' +
            'marked __v_skip. The vue-instantsearch internals it patches have likely changed — ' +
            'revisit the mixin in bootstrap.js or the memory leak returns.'
        )
      }

      if (typeof this.updateState === 'function' && this.instantSearchInstance) {
        const updateState = this.updateState
        this.updateState = (state, isFirstRender) => {
          if (state && typeof state === 'object' && !Object.isFrozen(state)) {
            Object.freeze(state)
          }
          return updateState.call(this, state, isFirstRender)
        }
      }
    }
  })

  // vue-gtag 3 builds the plugin up front rather than taking config through
  // use(), and reads the router from its own options instead of a third argument.
  app.use(
    createGtag({
      tagId: FONTAWESOME.GOOGLE_TRACKING_ID,
      disableScriptLoad: true,
      enabled: FONTAWESOME.GOOGLE_TRACKING_ENABLED === 'yes',
      pageTracker: { router }
    })
  )

  app.use(dashboard)

  app.component('site-link', SiteLink(app, { defaultLayout: WEB_AWESOME_LAYOUT }))

  // Vue 3 dropped `config.devtools` and `config.silent`; devtools presence is
  // decided by the build, and warnings are compiled out of production builds.
  if (FONTAWESOME.ENV !== 'production') {
    app.config.performance = true
  }

  return { router }
}
